import { Grid, Paper, Stack, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useEffect } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import AumAndFreshnessCard from './components/AumAndFreshnessCard';
import CoverageCard from './components/CoverageCard';
import ProviderChart from './components/ProviderChart';
import ProviderFeedStatusTable from './components/ProviderFeedStatusTable';
import useConnectedAssetsSummary from './hooks/useConnectedAssetsSummary';
import useConnectedAssetsProviders from './hooks/useConnectedAssetsProviders';

export default function ConnectedAssetsPage() {
  const { setSidebarCollapsed } = useAppContainerContext();

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useConnectedAssetsSummary();
  const {
    data: providers,
    isLoading: isProvidersLoading,
    isError: isProvidersError,
  } = useConnectedAssetsProviders();

  const isLoading = isSummaryLoading || isProvidersLoading;
  const isError = isSummaryError || isProvidersError;

  if (isLoading) return <PageLoader />;
  if (isError) return <GenericErrorPage error="Failed to load connected assets data." />;

  if (summary?.totalFeedProducts === 0) {
    return (
      <Text c="dimmed" size="lg">
        Your feed is being configured. You will see your coverage here once it is active.
      </Text>
    );
  }

  return (
    <Paper variant="content" p="xxl">
      <Stack gap="lg">
        <Text size="displayMd" fw={FONT_WEIGHTS.medium} c="gray.10">
          Connected Assets
        </Text>
        <Grid>
          <Grid.Col span={6}>
            <CoverageCard
              matchedProducts={summary?.matchedProducts ?? 0}
              totalFeedProducts={summary?.totalFeedProducts ?? 0}
              unmatchedProducts={summary?.unmatchedProducts ?? 0}
              coveragePercentage={summary?.coveragePercentage ?? 0}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <AumAndFreshnessCard
              connectedAum={summary?.connectedAum ?? 0}
              healthyProviderCount={
                providers?.providers.filter(p => p.health === 'HEALTHY').length ?? 0
              }
              totalProviderCount={providers?.providers.length ?? 0}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <ProviderChart providers={providers?.providers ?? []} />
          </Grid.Col>
        </Grid>
        <ProviderFeedStatusTable providers={providers?.providers ?? []} onFixClick={() => {}} />
      </Stack>
    </Paper>
  );
}
